import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useCallback, useMemo, useRef, useState } from 'react'
import {
  getAbp5FileDownloadUrl,
  getAbp5FileJobsByDocument,
  getLatestAbp5FileJob,
  postGenerateAbp5File,
} from 'modules/annual-benefit-planning/modules/approval/infra/api/config.api'
import { APPROVAL_QUERY_KEYS } from 'modules/annual-benefit-planning/modules/approval/infra/react-query/approval.query-keys'
import { ABP5_FILE_MESSAGES } from '../abp-status-messages'
import type { OepTargetFileJob } from '../types'
import {
  type GeneratedWorkbookJobsResult,
  isInFlightJob,
  useGeneratedWorkbookJobs,
} from './useGeneratedWorkbookJobs'

export { FILE_INITIAL_REVIEW_UNAVAILABLE_MESSAGE } from '../abp-status-messages'

const POLL_INTERVAL_MS = 5000

const EMPTY_JOBS_BY_DOCUMENT: ReadonlyMap<string, OepTargetFileJob> = new Map()

interface Options {
  readonly clientId: string
  /** When false, skips ABP-5 job queries (feature flag off). Default true. */
  readonly enabled?: boolean
}

// EMS-11146 Decisions #29/#30: ABP-5 Generate is per upload, so the
// client-wide flags of the shared workbook hook are replaced by per-document
// ones.
export interface UseAbp5FileJobsResult
  extends Omit<
    GeneratedWorkbookJobsResult,
    'isGenerateDisabled' | 'isGenerating' | 'generatingDocumentId'
  > {
  /** True while this upload's generate request or ABP-5 job is in flight. */
  readonly isGenerateDisabledFor: (documentId: string) => boolean
  /** True while this upload's generate request is in flight. */
  readonly isGeneratingFor: (documentId: string) => boolean
  /** Latest ABP-5 job keyed by analysis documentId. */
  readonly jobsByDocumentId: ReadonlyMap<string, OepTargetFileJob>
  readonly isJobsByDocumentLoading: boolean
}

function buildJobsByDocumentId(
  jobs: readonly OepTargetFileJob[] | undefined
): ReadonlyMap<string, OepTargetFileJob> {
  if (jobs === undefined || jobs.length === 0) {
    return EMPTY_JOBS_BY_DOCUMENT
  }
  const map = new Map<string, OepTargetFileJob>()
  for (const job of jobs) {
    if (job.documentId != null && job.documentId.length > 0) {
      map.set(job.documentId, job)
    }
  }
  return map
}

/** ABP-5 generation for one uploaded analysis document. */
export function useAbp5FileJobs({
  clientId,
  enabled = true,
}: Options): UseAbp5FileJobsResult {
  const queryClient = useQueryClient()
  const byDocumentQueryKey =
    APPROVAL_QUERY_KEYS.abp5FileJobsByDocument(clientId)

  const fetchLatestJob = useCallback(
    () => getLatestAbp5FileJob(clientId),
    [clientId]
  )
  const generateJob = useCallback(
    (documentId?: string) => {
      if (documentId === undefined || documentId === '') {
        return Promise.reject(
          new Error('Select an analysis file before generating an ABP file.')
        )
      }
      return postGenerateAbp5File(clientId, documentId)
    },
    [clientId]
  )
  const fetchDownloadUrl = useCallback(
    (jobId: string) => getAbp5FileDownloadUrl(clientId, jobId),
    [clientId]
  )

  const workbookJobs = useGeneratedWorkbookJobs({
    queryKey: APPROVAL_QUERY_KEYS.latestAbp5FileJob(clientId),
    fetchLatestJob,
    generateJob,
    fetchDownloadUrl,
    messages: ABP5_FILE_MESSAGES,
    enabled,
  })

  const { data: byDocumentJobs, isLoading: isJobsByDocumentLoading } = useQuery(
    {
      queryKey: byDocumentQueryKey,
      queryFn: () => getAbp5FileJobsByDocument(clientId),
      enabled,
      refetchInterval: (query) => {
        const jobs = query.state.data
        if (jobs === undefined) return false
        return jobs.some((job) => isInFlightJob(job)) ? POLL_INTERVAL_MS : false
      },
    }
  )

  const jobsByDocumentId = useMemo(
    () => buildJobsByDocumentId(byDocumentJobs),
    [byDocumentJobs]
  )

  // One mutation tracks only its latest call, so the uploads whose generate
  // request is still in flight are tracked here (several can run at once).
  const [generatingDocumentIds, setGeneratingDocumentIds] = useState<
    ReadonlySet<string>
  >(() => new Set())

  // `generatingDocumentIds` only updates after a re-render; this blocks a
  // second request for the same upload at once (double click).
  const inFlightRef = useRef(new Set<string>())

  const { handleGenerate: generateWorkbook } = workbookJobs
  const handleGenerate = useCallback(
    async (documentId?: string) => {
      if (documentId === undefined || documentId === '') {
        await generateWorkbook(documentId)
        return
      }
      if (inFlightRef.current.has(documentId)) return
      inFlightRef.current.add(documentId)
      setGeneratingDocumentIds((ids) => new Set(ids).add(documentId))
      try {
        await generateWorkbook(documentId)
        // Keep the upload busy until its new job is in jobsByDocumentId, so
        // it goes straight from "generating" to its running state (AC-41).
        await queryClient.invalidateQueries({ queryKey: byDocumentQueryKey })
      } finally {
        inFlightRef.current.delete(documentId)
        setGeneratingDocumentIds((ids) => {
          const next = new Set(ids)
          next.delete(documentId)
          return next
        })
      }
    },
    [generateWorkbook, queryClient, byDocumentQueryKey]
  )

  const isGeneratingFor = useCallback(
    (documentId: string) => generatingDocumentIds.has(documentId),
    [generatingDocumentIds]
  )

  const isGenerateDisabledFor = useCallback(
    (documentId: string) =>
      generatingDocumentIds.has(documentId) ||
      isInFlightJob(jobsByDocumentId.get(documentId) ?? null),
    [generatingDocumentIds, jobsByDocumentId]
  )

  return {
    latestJob: workbookJobs.latestJob,
    isLoading: workbookJobs.isLoading,
    handleDownload: workbookJobs.handleDownload,
    handleGenerate,
    isGenerateDisabledFor,
    isGeneratingFor,
    jobsByDocumentId,
    isJobsByDocumentLoading,
  }
}
